import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import type { Node as RFNode, Edge as RFEdge } from 'reactflow';
import type { NodeData } from '@/types/node.types';
import type { LinkData } from '@/types/link.types';
import type { Canvas } from '@/types/canvas.types';
import {
  EXPORT_FORMAT,
  EXPORT_VERSION,
  type ExportedLink,
  type ExportedNode,
  type StratMapExport,
} from '@/types/export.types';

// ---- helpers --------------------------------------------------------------

function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'canvas';
}

function triggerDownload(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// ---- PNG / PDF ------------------------------------------------------------

async function captureFlowAsPng(
  flowRoot: HTMLElement,
  backgroundColor: string,
): Promise<string> {
  // Hide the controls overlay (minimap, attribution, bottom bar overlays) during capture.
  const hidden: Array<{ el: HTMLElement; prev: string }> = [];
  const hideSelectors = ['.react-flow__minimap', '.react-flow__controls', '.react-flow__attribution'];
  for (const sel of hideSelectors) {
    flowRoot.querySelectorAll<HTMLElement>(sel).forEach((el) => {
      hidden.push({ el, prev: el.style.visibility });
      el.style.visibility = 'hidden';
    });
  }
  try {
    return await toPng(flowRoot, {
      backgroundColor,
      pixelRatio: 2,
      cacheBust: true,
      // Filter out foreign iframes / scripts that might fail CORS
      filter: (node) => {
        const el = node as HTMLElement;
        return !(el.tagName === 'IFRAME');
      },
    });
  } finally {
    for (const { el, prev } of hidden) el.style.visibility = prev;
  }
}

/** Render the React Flow pane to a PNG and trigger a download. */
export async function exportToPNG(canvasName: string, flowRoot: HTMLElement): Promise<void> {
  const bg = getComputedStyle(document.body).backgroundColor || '#ffffff';
  const dataUrl = await captureFlowAsPng(flowRoot, bg);
  triggerDownload(dataUrl, `${slugify(canvasName)}.png`);
}

/** Render the canvas to PDF (A4 landscape, cartouche minimal). */
export async function exportToPDF(canvasName: string, flowRoot: HTMLElement): Promise<void> {
  const dataUrl = await captureFlowAsPng(flowRoot, '#ffffff');

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const headerH = 12;

  // Header: canvas name + date
  pdf.setFontSize(14);
  pdf.text(canvasName, margin, margin + 6);
  pdf.setFontSize(9);
  pdf.setTextColor(120);
  pdf.text(new Date().toLocaleString('fr-FR'), pageW - margin, margin + 6, { align: 'right' });
  pdf.setTextColor(0);

  // Image area
  const areaX = margin;
  const areaY = margin + headerH;
  const areaW = pageW - margin * 2;
  const areaH = pageH - areaY - margin;

  // Fit image preserving aspect ratio
  const img = new Image();
  img.src = dataUrl;
  await new Promise<void>((resolve) => {
    img.onload = () => resolve();
  });
  const imgRatio = img.width / img.height;
  const areaRatio = areaW / areaH;
  let drawW: number;
  let drawH: number;
  if (imgRatio > areaRatio) {
    drawW = areaW;
    drawH = areaW / imgRatio;
  } else {
    drawH = areaH;
    drawW = areaH * imgRatio;
  }
  const drawX = areaX + (areaW - drawW) / 2;
  const drawY = areaY + (areaH - drawH) / 2;

  pdf.addImage(dataUrl, 'PNG', drawX, drawY, drawW, drawH);
  pdf.save(`${slugify(canvasName)}.pdf`);
}

// ---- JSON -----------------------------------------------------------------

function buildExportJSON(
  canvas: Canvas,
  nodes: RFNode<NodeData>[],
  edges: RFEdge<LinkData>[],
): StratMapExport {
  const toNum = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const exportedNodes: ExportedNode[] = nodes
    .filter((n) => n.type !== 'group')
    .map((n) => ({
      localId: n.id,
      type: n.data.type,
      title: n.data.title,
      description: n.data.description,
      status: n.data.status,
      priority: n.data.priority,
      tags: n.data.tags,
      position: { x: n.position.x, y: n.position.y },
      budgetPlanned: toNum(n.data.budgetPlanned),
      budgetSpent: toNum(n.data.budgetSpent),
      effortPlannedDays: toNum(n.data.effortPlannedDays),
      effortSpentDays: toNum(n.data.effortSpentDays),
      progress: toNum(n.data.progress),
    }));

  const exportedLinks: ExportedLink[] = edges
    .filter((e): e is RFEdge<LinkData> & { data: LinkData } => e.data != null)
    .map((e) => ({
      sourceLocalId: e.source,
      targetLocalId: e.target,
      type: e.data.linkType,
      intensity: e.data.intensity,
      label: e.data.label,
      description: e.data.description,
    }));

  return {
    format: EXPORT_FORMAT,
    version: EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    canvas: {
      name: canvas.name,
      description: canvas.description,
    },
    nodes: exportedNodes,
    links: exportedLinks,
  };
}

/** Serialize the canvas to a versioned JSON file and trigger a download. */
export function exportToJSON(
  canvas: Canvas,
  nodes: RFNode<NodeData>[],
  edges: RFEdge<LinkData>[],
): void {
  const payload = buildExportJSON(canvas, nodes, edges);
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, `${slugify(canvas.name)}.stratmap.json`);
  URL.revokeObjectURL(url);
}
